import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "banner",
    "collapsed",
    "mainContent",
    "messageForm",
    "messageTextarea",
    "chat",
    "recipientType",
    "loanOfficerInfo",
    "realtorInfo",
    "messageFormElement",
    "valueReportId",
  ];

  connect() {
    this.handleCallNow = this.debounce(this.handleCallNow.bind(this), 300);
    document.addEventListener("triggerMessageForm", (event) => {
      this.expandBanner();

      document.triggerMessageFormFired = true;

      if (event.detail && event.detail.recipientType === "realtor") {
        if (event.detail.valueReportId && this.hasValueReportIdTarget) {
          this.valueReportIdTarget.value = event.detail.valueReportId;
        }
        this.setRecipientToRealtor();
      } else {
        this.setRecipientToLoanOfficer();
      }
    });

    this.logUserInteraction = this.debounce(
      this.logUserInteraction.bind(this),
      300,
    );
    if (window.innerWidth <= 768) {
      this.collapseBanner();
    } else {
      this.expandBanner();
    }
  }

  setRecipientToRealtor() {
    if (this.hasRecipientTypeTarget) {
      this.recipientTypeTarget.value = "realtor";
    }

    if (this.hasMessageFormElementTarget) {
      const lenderId = this.messageFormElementTarget.dataset.lenderId;
      const newUrl = `/communications/realtor/${lenderId}/message`;
      this.messageFormElementTarget.dataset.url = newUrl;
    }

    if (this.hasLoanOfficerInfoTarget && this.hasRealtorInfoTarget) {
      this.loanOfficerInfoTarget.style.display = "none";
      this.realtorInfoTarget.style.display = "block";
    }

    this.toggleToMessageForm();
  }

  setRecipientToLoanOfficer() {
    if (this.hasRecipientTypeTarget) {
      this.recipientTypeTarget.value = "loan_officer";
    }

    if (this.hasMessageFormElementTarget) {
      const lenderId = this.messageFormElementTarget.dataset.lenderId;
      const newUrl = `/communications/loan_officer/${lenderId}/notify_message`;
      this.messageFormElementTarget.dataset.url = newUrl;
    }

    if (this.hasLoanOfficerInfoTarget && this.hasRealtorInfoTarget) {
      this.loanOfficerInfoTarget.style.display = "block";
      this.realtorInfoTarget.style.display = "none";
    }

    if (
      window.BOTSPLASH_LOADED &&
      document.getElementById("botsplash-chat-root")
    ) {
      this.startBotsplash();
    } else {
      this.toggleToMessageForm();
    }
  }

  debounce(func, delay) {
    let debounceTimer;
    return function (...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  expandBanner() {
    this.bannerTarget.style.display = "block";
    this.collapsedTarget.style.display = "none";
    this.mainContentTarget.style.display = "block";
    this.messageFormTarget.style.display = "none";
    this.fadeIn(this.mainContentTarget, 1);
  }

  collapseBanner() {
    this.bannerTarget.style.display = "none";
    this.collapsedTarget.style.display = "flex";
    this.messageFormTarget.style.display = "none";
  }

  toggleToMessageForm() {
    if (!document.triggerMessageFormFired && this.hasRecipientTypeTarget) {
      this.recipientTypeTarget.value = "loan_officer";

      if (this.hasMessageFormElementTarget) {
        const lenderId = this.messageFormElementTarget.dataset.lenderId;
        const newUrl = `/communications/loan_officer/${lenderId}/notify_message`;
        this.messageFormElementTarget.dataset.url = newUrl;
      }

      if (this.hasLoanOfficerInfoTarget && this.hasRealtorInfoTarget) {
        this.loanOfficerInfoTarget.style.display = "block";
        this.realtorInfoTarget.style.display = "none";
      }
    }

    document.triggerMessageFormFired = false;

    const currentUrl = window.location.href;
    const recipientType = this.hasRecipientTypeTarget
      ? this.recipientTypeTarget.value
      : "loan_officer";
    const actionItem =
      recipientType === "realtor" ? "message_form_realtor" : "message_form";

    this.logUserInteraction(actionItem, currentUrl);
    this.fadeOut(this.mainContentTarget, 250, () => {
      this.mainContentTarget.style.display = "none";
      this.fadeIn(this.messageFormTarget, 250);
    });
  }

  toggleToMainContent() {
    this.fadeOut(this.messageFormTarget, 250, () => {
      this.messageFormTarget.style.display = "none";
      this.fadeIn(this.mainContentTarget, 250);
    });
  }

  async sendMessage(event) {
    event.preventDefault();
    const message = this.messageTextareaTarget.value;

    const form = event.currentTarget;
    const url = form.dataset.url;

    const recipientType = this.hasRecipientTypeTarget
      ? this.recipientTypeTarget.value
      : "loan_officer";
    const actionItem =
      recipientType === "realtor" ? "send_message_to_realtor" : "send_message";

    const requestData = { message };

    if (this.hasValueReportIdTarget && this.valueReportIdTarget.value) {
      requestData.value_report_id = this.valueReportIdTarget.value;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        this.logUserInteraction(actionItem, window.location.href);
        alert("Message sent successfully!");
        this.toggleToMainContent();
        this.messageTextareaTarget.value = "";
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to send message: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred while sending the message.");
    }
  }

  async logUserInteraction(actionItem, destinationUrl) {
    const response = await fetch("/user_interactions/log_action", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      body: JSON.stringify({
        action_item: actionItem,
        destination_url: destinationUrl,
      }),
    });

    if (!response.ok) {
      console.error("Failed to log user interaction");
    }
  }

  handleCallNow(event) {
    event.preventDefault();
    const telUrl = event.currentTarget.href;
    this.logUserInteraction("call_now", telUrl).then(() => {
      window.location.href = telUrl;
    });
  }

  fadeOut(element, duration, callback) {
    element.style.transition = `opacity ${duration}ms`;
    element.style.opacity = 0;
    setTimeout(() => {
      callback();
    }, duration);
  }

  fadeIn(element, duration) {
    element.style.display = "block";
    element.style.opacity = 0;
    element.style.transition = `opacity ${duration}ms`;
    setTimeout(() => {
      element.style.opacity = 1;
    }, 10);
  }

  hideBotsplash() {
    const botElement = document.getElementById("botsplash-chat-root");
    if (botElement) {
      botElement.style.visibility = "hidden";
    }
    this.bannerTarget.style.visibility = "visible";
    this.collapsedTarget.style.visibility = "visible";
  }

  startBotsplash() {
    const botElement = document.getElementById("botsplash-chat-root");
    if (window.BOTSPLASH_LOADED && botElement) {
      document.addEventListener(
        "botsplash:close",
        this.hideBotsplash.bind(this),
      );
      window.$botsplash.addChat();
      botElement.style.visibility = "visible";
      this.bannerTarget.style.visibility = "hidden";
      this.collapsedTarget.style.visibility = "hidden";
    } else {
      this.toggleToMessageForm();
    }
  }
}
